import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import '../../styles/pp-activation.css';
import { derivePlanState, setToastSuppression } from '../../utils/planState';
import { useSupportContact } from '../../queries/profile';
import { whatsappDigits } from '../../pages/Devices/Component/Health/model';

const RECHECK_MS = 60_000;

// "+966583272051" -> "+966 58 327 2051" (the prototype's grouping); other numbers are shown as stored.
export function formatPhone(e164) {
  const m = /^\+966(\d{2})(\d{3})(\d{4})$/.exec(String(e164 || ''));
  return m ? `+966 ${m[1]} ${m[2]} ${m[3]}` : String(e164 || '');
}

const Svg = ({ children, color = 'currentColor', size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{children}</svg>
);

// Page title shown above the card (the sidebar item the user picked).
const TITLES = [
  ['/dashboard', 'nav.home'], ['/create', 'nav.create'], ['/devices', 'nav.screens'], ['/screens', 'nav.screens'],
  ['/content', 'nav.content'], ['/media', 'nav.content'], ['/playlist', 'nav.content'], ['/links', 'nav.content'],
  ['/campaigns', 'nav.shows'], ['/interactive', 'nav.shows'], ['/schedule', 'nav.schedule'],
  ['/remote-control', 'nav.remote_control'], ['/hardware', 'nav.scenes'], ['/apps', 'nav.apps'],
  ['/customers', 'nav.customers'], ['/marketing', 'nav.marketing'], ['/loyalty', 'nav.loyalty'], ['/money', 'nav.money'],
  ['/reports', 'nav.reports'], ['/users', 'nav.users'],
];

function formatDate(value, lng) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const locale = lng.startsWith('ar') ? 'ar-u-nu-latn' : 'en-GB';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

/**
 * Replaces the content area of every page for a company with no active package
 * (CompanyDetailsRouter renders it inside the normal Layout, so the sidebar
 * stays). Two variants: pending activation (never had a plan) and ended.
 * Re-checks on focus, every 60 s, and on the button; the router swaps it for
 * the app the moment /company/details says a plan is active.
 */
export default function NoPackageState({ details }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { status, last } = derivePlanState(details);
  const ended = status === 'ended';
  const support = useSupportContact(true).data;
  const digits = whatsappDigits(support?.whatsapp);
  const [checking, setChecking] = useState(false);
  const [msg, setMsg] = useState('');
  const timer = useRef(null);
  const a = (k, o) => t(`activation.${ended ? 'ended' : 'pending'}.${k}`, o);

  useEffect(() => { setToastSuppression(true); return () => setToastSuppression(false); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  const refetch = useCallback(() => queryClient.refetchQueries('getUserSubscription'), [queryClient]);

  // Automatic re-check: every 60 s while visible, and whenever the tab/window regains focus.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') refetch(); };
    const id = setInterval(tick, RECHECK_MS);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); window.removeEventListener('focus', tick); document.removeEventListener('visibilitychange', tick); };
  }, [refetch]);

  const onRecheck = async () => {
    if (checking) return;
    setChecking(true);
    let ok = true;
    try { await refetch(); } catch { ok = false; }
    setChecking(false);
    // If a plan is now active this component is already being unmounted by the router.
    setMsg(ok ? a('toast') : t('activation.recheck_failed'));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(''), 3200);
  };

  const titleKey = (TITLES.find(([p]) => pathname === p || pathname.startsWith(p + '/')) || [])[1] || 'nav.home';
  const wa = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4 20l1.3-3.9A8 8 0 1 1 8 19.1z" /><path d="M9 9.5c.3 1.8 1.7 3.6 3.6 4.5l1.2-1.1 2 .9-.4 1.6c-3.4.4-7-3.2-6.7-6.6L10.3 8l.8 2z" />
    </svg>
  );
  const number = digits ? <span className="ppn-num" dir="ltr">{formatPhone(support.whatsapp)}</span> : null;

  return (
    <div className="ppn">
      <div className="ppn-top"><div><h1>{t(titleKey)}</h1><p>{details?.company?.name}</p></div></div>
      <section className="ppn-card" aria-live="polite">
        <div className={`ppn-ic${ended ? ' crit' : ''}`}>
          {ended
            ? <Svg size={30} color="var(--ppn-coral)"><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5v.5" /></Svg>
            : <Svg size={30} color="var(--ppn-honey-deep)"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Svg>}
        </div>
        <div><h2>{a('title')}</h2><p className="ppn-lead">{a('lead')}</p></div>

        {ended ? (
          <div className="ppn-facts">
            <div className="ppn-fact"><span>{t('activation.ended.package')}</span><b>{last?.package_title || '—'}</b></div>
            <div className="ppn-fact"><span>{t('activation.ended.ended_on')}</span><b>{formatDate(last?.end_date, i18n.language) || '—'}</b></div>
          </div>
        ) : (
          <ul className="ppn-steps">
            <li className="done"><i><Svg size={16}><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg></i><div><b>{t('activation.pending.step_account')}</b></div><small>{t('activation.pending.done')}</small></li>
            <li className="done"><i><Svg size={16}><path d="M5 12.5l4.5 4.5L19 7.5" /></Svg></i><div><b>{t('activation.pending.step_email')}</b></div><small>{t('activation.pending.done')}</small></li>
            <li className="wait"><i><Svg size={16}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></Svg></i><div><b>{t('activation.pending.step_plan')}</b><br /><small className="ppn-sub">{t('activation.pending.step_plan_sub')}</small></div><small>{t('activation.pending.waiting')}</small></li>
          </ul>
        )}

        <div className="ppn-acts">
          {digits && <a className="ppn-btn p" href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer">{wa}{a('whatsapp')}</a>}
          <button className="ppn-btn o" type="button" disabled={checking} onClick={onRecheck}>
            {checking ? (<><span className="ppn-spin" />{t('activation.checking')}</>) : a('recheck')}
          </button>
        </div>
        <p className="ppn-note">
          <Svg size={18}>{ended ? <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5v.5" /></> : <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M3.5 6.5l8.5 6.5 8.5-6.5" /></>}</Svg>
          <span>{a('note')}{number && <> {t('activation.whatsapp_label')} {number}</>}</span>
        </p>
      </section>
      {msg && <div className="ppn-toast" role="status">{msg}</div>}
    </div>
  );
}
